// W3C Geolocation helpers + Photon relay calls.
import type { LatLon } from '../src/domain/geo.ts';
import { api } from './lib.ts';

export interface Place extends LatLon {
  label: string;
  name?: string;
  kind?: string | null;
  extId?: string | null;
  area?: string | null;
  accuracy?: number;
  /** Where the position came from; stored with the member (gps | address | map). */
  source?: 'gps' | 'address' | 'map';
}

export const canGps = () => window.isSecureContext && 'geolocation' in navigator;

function geoError(e: GeolocationPositionError) {
  return new Error(
    e.code === 1
      ? 'Location permission denied. Allow it in your browser settings, or type an address.'
      : e.code === 3
        ? 'Getting your location took too long. Try again or type an address.'
        : 'Could not determine your location.',
  );
}

export function locate(): Promise<LatLon & { accuracy: number }> {
  return new Promise((resolve, reject) => {
    if (!canGps()) return reject(new Error('GPS needs the secure (https) link.'));
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
      (e) => reject(geoError(e)),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 },
    );
  });
}

/** Live position stream; returns a stop function. Only a denied permission is fatal: phones report
 * transient "position unavailable"/timeout errors while moving, and the watch keeps going. */
export function watch(onPos: (p: LatLon & { accuracy: number }) => void, onFatal: (e: Error) => void) {
  const id = navigator.geolocation.watchPosition(
    (p) => onPos({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
    (e) => {
      if (e.code === e.PERMISSION_DENIED) onFatal(geoError(e));
    },
    { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
  );
  return () => navigator.geolocation.clearWatch(id);
}

export async function gpsAllowed() {
  if (!canGps()) return false;
  try {
    return (await navigator.permissions.query({ name: 'geolocation' })).state === 'granted';
  } catch {
    return false;
  }
}

export async function reverse(p: LatLon): Promise<Place> {
  try {
    const r = await api<Place | null>(`/api/geo/reverse?lat=${p.lat}&lon=${p.lon}`);
    return { ...p, label: r?.label || 'Pinned on the map', name: r?.name, area: r?.area, extId: null, source: 'map' };
  } catch {
    return { ...p, label: 'Pinned on the map', source: 'map' };
  }
}

export const search = (q: string, near?: LatLon | null) => {
  const qs = new URLSearchParams({ q, lang: (navigator.language || 'en').slice(0, 2) });
  if (near) {
    qs.set('lat', String(near.lat));
    qs.set('lon', String(near.lon));
  }
  return api<Place[]>(`/api/geo/search?${qs}`);
};

export function directionsUrl(to: LatLon, from: LatLon | null, gmapsMode: string) {
  const u = new URL('https://www.google.com/maps/dir/');
  u.searchParams.set('api', '1');
  if (from) u.searchParams.set('origin', `${from.lat},${from.lon}`);
  u.searchParams.set('destination', `${to.lat},${to.lon}`);
  u.searchParams.set('travelmode', gmapsMode);
  return u.toString();
}
const APPLE_DIR: Record<string, string> = { walking: 'w', driving: 'd', transit: 'r' };
const isApple = () => /iPhone|iPad|iPod|Macintosh/.test(navigator.userAgent);

/** Native maps app: Apple Maps on Apple devices (they ignore geo: links), RFC 5870 geo: URI elsewhere. */
export function nativeMapsUrl(p: LatLon, label: string, gmapsMode: string) {
  if (!isApple()) return geoUri(p, label);
  const u = new URL('https://maps.apple.com/');
  u.searchParams.set('daddr', `${p.lat},${p.lon}`);
  u.searchParams.set('q', label);
  if (APPLE_DIR[gmapsMode]) u.searchParams.set('dirflg', APPLE_DIR[gmapsMode]);
  return u.toString();
}
export const nativeMapsName = () => (isApple() ? 'Apple Maps' : 'Maps app');

/** RFC 5870 geo URI: opens the native maps app on Android. */
export const geoUri = (p: LatLon, label?: string) =>
  `geo:${p.lat.toFixed(6)},${p.lon.toFixed(6)}${label ? `?q=${p.lat},${p.lon}(${encodeURIComponent(label)})` : ''}`;
